import React from 'react';

export const PrivacyPolicy: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 py-16">
      <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>

      <div className="prose prose-lg max-w-none text-gray-600 space-y-6">
        <p className="text-sm text-gray-500">Last Updated: June 1, 2026</p>

        <section>
          <p>
            This Privacy Policy explains what information TreadAI ("we", "us", "our") collects when you use our website (tread-ai.com) and mobile app, how we use it, who we share it with, and the choices you have.
          </p>
          <p>
            By using TreadAI you agree to the practices described in this policy. If you do not agree, please do not use the Service.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Information we collect</h2>

          <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">Tire and vehicle data you submit</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Tire images</strong> you upload for analysis</li>
            <li><strong>Vehicle identifiers</strong> you enter (VIN or license plate) used to resolve vehicle make/model/year</li>
            <li><strong>Inspection results</strong> generated by our analysis (tread depth, condition, recommendations)</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">Device and usage data (automatic)</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Anonymous device identifier</strong> — a randomly generated UUID stored in the device's secure storage. We do not collect your name, email, phone number, or Apple ID.</li>
            <li><strong>Approximate location</strong> (only when you tap "Find nearby tire shops") — used once to query nearby shops; not stored.</li>
            <li><strong>Basic device information</strong> — device model, OS version, app version. Used for crash diagnostics and analytics.</li>
            <li><strong>Usage analytics</strong> — which screens you visit, feature usage. Anonymous and aggregated.</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">Purchase data</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>If you make an in-app purchase, the transaction is processed by Apple. We receive a record of the purchase (product purchased, date) tied to your anonymous device ID via our purchase analytics provider, RevenueCat.</li>
            <li>We do <strong>not</strong> receive your credit card information, Apple ID email, or billing address. Apple handles payment processing.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How we use the information</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>To analyze your tire images and return inspection results</li>
            <li>To resolve vehicle information from VIN or license plate</li>
            <li>To find nearby tire shops when you request them</li>
            <li>To track entitlements (free inspections used, credit balance, subscription status)</li>
            <li>To improve our AI models and the accuracy of inspection results</li>
            <li>To detect bugs, crashes, and abuse</li>
            <li>To comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Third-party services we use</h2>
          <p>
            We use the following third parties to operate TreadAI. Each has its own privacy policy governing data they receive.
          </p>
          <ul className="list-disc pl-6 space-y-3">
            <li>
              <strong>RevenueCat</strong> — manages in-app purchases and subscriptions on our behalf. Receives: anonymous device ID, purchase events, subscription status.
              <a href="https://www.revenuecat.com/privacy" className="text-blue-600 underline ml-1">Privacy policy</a>
            </li>
            <li>
              <strong>Apple App Store / StoreKit</strong> — processes all in-app purchases. We never see your credit card or Apple ID.
              <a href="https://www.apple.com/legal/privacy/" className="text-blue-600 underline ml-1">Privacy policy</a>
            </li>
            <li>
              <strong>OpenAI</strong> — used for image analysis (DOT code reading, tire-size scanning). Receives: the specific tire image you submit. OpenAI does not train on data submitted via the API.
              <a href="https://openai.com/policies/api-data-usage-policies" className="text-blue-600 underline ml-1">Data policy</a>
            </li>
            <li>
              <strong>Google Places API</strong> — used to look up nearby tire shops. Receives: approximate latitude/longitude at the moment of the search.
              <a href="https://policies.google.com/privacy" className="text-blue-600 underline ml-1">Privacy policy</a>
            </li>
            <li>
              <strong>Our backend hosted on AWS EC2</strong> — runs the AI analysis pipeline and stores inspection history.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data we do NOT collect</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>We do not require you to create an account or provide an email address</li>
            <li>We do not collect your name, phone number, or address</li>
            <li>We do not access your contacts, calendar, microphone, or unrelated photos</li>
            <li>We do not track you across other apps or websites</li>
            <li>We do not sell your personal data to any party</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data retention</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Tire images</strong>: retained to improve AI accuracy. You can request deletion (see Section 7).</li>
            <li><strong>Inspection results</strong>: retained until you uninstall the app or request deletion.</li>
            <li><strong>Anonymous device ID</strong>: persists on your device in secure storage until you uninstall and factory-reset the device.</li>
            <li><strong>Purchase records</strong>: retained for tax and accounting purposes as required by law (typically 7 years).</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Children's privacy</h2>
          <p>
            TreadAI is not directed to children under 13. We do not knowingly collect personal information from children. If you believe a child has provided us information, please contact us and we will delete it.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your rights</h2>
          <p>You have the right to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Request a copy of the data associated with your anonymous device ID</li>
            <li>Request deletion of your data</li>
            <li>Opt out of analytics (uninstall the app)</li>
            <li>If you are in the EU/UK: rights under GDPR including access, rectification, erasure, restriction, portability, and objection</li>
            <li>If you are in California: rights under CCPA including the right to know, delete, and not be discriminated against for exercising your rights</li>
          </ul>
          <p className="mt-4">
            To exercise any of these rights, contact us at the email below.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Data security</h2>
          <p>
            We use industry-standard measures (HTTPS in transit, encrypted storage at rest, restricted access controls) to protect your data. No system is 100% secure; if a breach occurs, we will notify affected users as required by law.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">9. International users</h2>
          <p>
            TreadAI is operated from the United States. If you use the Service from outside the U.S., your data is transferred to and processed in the U.S., which may have different data protection laws than your country.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to this policy</h2>
          <p>
            We may update this Privacy Policy from time to time. When we do, we will update the "Last Updated" date at the top of this page. Material changes will be announced in the app or via a notice on this page.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact</h2>
          <p>
            For privacy questions, data requests, or to exercise any of the rights above, please contact us at:
          </p>
          <p className="mt-2">
            <strong>Email:</strong> <a href="mailto:privacy@tread-ai.com" className="text-blue-600 underline">privacy@tread-ai.com</a>
          </p>
        </section>
      </div>
    </div>
  );
};
